import { getRegistryList } from '@/api/backend/registry';
import { RegistryType } from '@/api/backend/types';
import { RegistryStatusBadge } from '@/components/registry-status-badge';
import { ResponsiveDataView } from '@/components/responsive-data-view';
import { REGISTRY_STATUSES, ZONE_CODES } from '@/lib/constants';
import { formatIsoDate, getLocaleObject } from '@/lib/utils';
import {
  Card,
  DataTable,
  DataTableColumnType,
  DataTableFilterConfig,
  DataTableSortConfig,
  Link,
  SearchInput,
} from '@opaz/ui-components';
import { useQuery } from '@tanstack/react-query';
import { useScrollToSection } from '@/hooks/use-scroll-to-section';
import { DASHBOARD_SECTIONS } from '@/lib/dashboard-sections';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const ContractsCard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  useScrollToSection(DASHBOARD_SECTIONS.contracts);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortConfig, setSortConfig] = useState<DataTableSortConfig | null>(null);
  const [filterConfig, setFilterConfig] = useState<DataTableFilterConfig | null>(null);
  const [tempSearchValue, setTempSearchValue] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');

  const { data: page, isLoading } = useQuery({
    queryKey: ['registries', 'RGT_CONTRACT', currentPage, sortConfig, filterConfig, searchValue],
    queryFn: () =>
      getRegistryList(
        'RGT_CONTRACT',
        {
          page: currentPage,
          size: 5,
        },
        sortConfig,
        filterConfig,
        {
          registryNumber: searchValue,
        },
      ),
  });

  const columns: DataTableColumnType<RegistryType>[] = [
    {
      title: t('dashboard.contracts.headings.contractNumber'),
      sortKey: 'recordNumber',
      render: (record: RegistryType) => (
        <Link
          onClick={() => {
            navigate({
              to: '/registry/contracts/$contractId',
              params: { contractId: record.registryNumber },
              search: { version: undefined },
            });
          }}
          className="w-[120px] text-start"
        >
          {record.registryNumber}
        </Link>
      ),
    },
    {
      title: t('dashboard.contracts.headings.name'),
      render: (record: RegistryType) =>
        t([`backend.codes.${record.registryCode}` as unknown as TemplateStringsArray, 'common.unknown']),
    },
    {
      title: t('dashboard.contracts.headings.beneficiary'),
      render: (record: RegistryType) => record.party2NameEn || t('common.unknown'),
    },
    {
      title: t('dashboard.permits.headings.zone'),
      sortKey: 'zone',
      render: (record: RegistryType) =>
        record.zoneCode ? t(`common.zoneCode.${record.zoneCode}`) : t('common.unknown'),
      filter: {
        type: 'select',
        key: 'zone',
        options: ZONE_CODES.map((zoneCode) => ({
          label: t(`common.zoneCode.${zoneCode}`),
          value: zoneCode,
        })),
      },
    },
    {
      title: t('dashboard.contracts.headings.contractEndDate'),
      sortKey: 'entries.endDate',
      render: (record: RegistryType) => formatIsoDate(record.endDate, i18n.language),
      filter: {
        type: 'date-range',
        key: 'endDateRange',
      },
    },
    {
      title: t('dashboard.contracts.headings.status'),
      sortKey: 'entries.recordStatus',
      align: 'end',
      render: (record: RegistryType) => {
        return <RegistryStatusBadge statusCode={record.statusCode} />;
      },
      filter: {
        type: 'select',
        key: 'status',
        options: REGISTRY_STATUSES.map((status) => ({
          label: t(`registry.status.${status}`),
          value: status,
        })),
      },
    },
  ];

  return (
    <Card id={DASHBOARD_SECTIONS.contracts} className="w-full scroll-mt-24 p-5 shadow-lg border-none">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold">{t('dashboard.tabs.contracts')}</h2>
      </div>

      <SearchInput
        className="max-w-md mb-5"
        placeholder={t('common.search')}
        disabled={isLoading}
        onChange={(e) => {
          setTempSearchValue(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            setSearchValue(tempSearchValue);
            setCurrentPage(0);
          }
        }}
        value={tempSearchValue || ''}
        onClear={() => {
          setTempSearchValue('');
          setSearchValue('');
          setCurrentPage(0);
        }}
        onSearchSubmit={() => {
          setSearchValue(tempSearchValue);
          setCurrentPage(0);
        }}
      />

      <ResponsiveDataView
        columns={columns}
        data={page?.content?.length ? [...page.content] : []}
        isLoading={isLoading}
        emptyMessage={t('common.table.emptyState')}
        currentPage={currentPage + 1}
        totalPages={page?.totalPages || 0}
        onPageChange={(page) => setCurrentPage(page - 1)}
        getRowKey={(record) => record.registryNumber}
        filterConfig={filterConfig}
        onFilterChange={(config) => {
          setCurrentPage(0);
          setFilterConfig(config);
        }}
      >
        <DataTable
          locale={getLocaleObject(i18n.language)}
          columns={columns}
          dataSource={page?.content?.length ? [...page.content] : []}
          isLoading={isLoading}
          currentPage={currentPage + 1}
          totalPages={page?.totalPages || 0}
          onPageChange={(page) => setCurrentPage(page - 1)}
          onSortChange={setSortConfig}
          onFilterChange={(config) => {
            setCurrentPage(0);
            setFilterConfig(config);
          }}
          dateFormatter={(value: Date) => formatIsoDate(value.toISOString(), i18n.language)}
          translations={{
            apply: t('common.table.filter.apply'),
            clear: t('common.table.filter.clear'),
            from: t('common.table.filter.from'),
            clearAllFilters: t('common.table.filter.clearAllFilters'),
            noData: t('common.table.emptyState'),
            nextPage: t('common.table.next'),
            previousPage: t('common.table.previous'),
          }}
        />
      </ResponsiveDataView>
    </Card>
  );
};
